import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, ArrowRight, X, Eye } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { inr, split4060 } from "@/api";
import { useLang, tr } from "@/i18n";

const ONE_DAY_ORDER = ["pkg-1day", "pkg-tripindi", "pkg-narayan-bali", "pkg-nag-bali", "pkg-pitru-dosha"];
const MULTI_DAY_ORDER = ["pkg-3day", "pkg-5day", "pkg-7day", "pkg-17day"];

const PackageCard = ({ pkg, index, onBook, onDetails, t }) => {
    const { advance, balance } = split4060(pkg.price);
    return (
        <Reveal delay={(index % 3) * 0.1} className="h-full">
            <motion.article
                data-testid={`package-card-${pkg.id}`}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="h-full flex flex-col bg-white rounded-2xl overflow-hidden border border-[#E6DBCF] shadow-[0_2px_24px_rgba(36,29,25,0.06)] hover:shadow-[0_16px_48px_rgba(156,55,11,0.14)] transition-shadow duration-500"
            >
                <div className="relative h-52 overflow-hidden bg-[#F4EDE2]">
                    {pkg.image ? (
                        <img
                            src={pkg.image}
                            alt={pkg.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#6E6259] text-sm">
                            Image coming soon
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#FFF8E7] border border-[#E5C158] text-[#9C370B] text-xs font-semibold tracking-wide">
                        {t(pkg.tag)}
                    </span>
                    <span className="absolute bottom-4 right-4 font-display text-white/70 text-3xl">
                        {String(index + 1).padStart(2, "0")}
                    </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs text-[#6E6259] mb-2.5">
                        <Clock size={14} className="text-[#C85A17]" />
                        <span data-testid={`package-duration-${pkg.id}`}>{t(pkg.duration)} {t("Ritual")}</span>
                    </div>
                    <h3 className="font-serif-alt font-semibold text-xl text-[#241D19] leading-snug">
                        {t(pkg.name)}
                    </h3>
                    <p className="mt-2.5 text-sm text-[#6E6259] leading-relaxed line-clamp-2">{t(pkg.description)}</p>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {pkg.locations.slice(0, 2).map((loc) => (
                            <span key={loc} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-[#F4EDE2] text-[#6E6259]">
                                <MapPin size={10} className="text-[#C85A17]" /> {loc}
                            </span>
                        ))}
                        {pkg.locations.length > 2 && (
                            <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#F4EDE2] text-[#6E6259]">
                                +{pkg.locations.length - 2} {t("more")}
                            </span>
                        )}
                    </div>

                    <div className="mt-5 pt-5 border-t border-[#E6DBCF]">
                        <div className="flex items-end justify-between mb-2.5">
                            <span className="text-[11px] uppercase tracking-[0.18em] text-[#6E6259]">{t("Total Price")}</span>
                            <span data-testid={`package-price-${pkg.id}`} className="font-display text-2xl font-semibold text-[#241D19]">
                                {inr(pkg.price)}
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden flex mb-2.5">
                            <div className="bg-[#C85A17]" style={{ width: "40%" }} />
                            <div className="bg-[#E5C158]" style={{ width: "60%" }} />
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <p className="text-[11px] text-[#6E6259]">{t("40% Advance")}</p>
                                <p data-testid={`package-advance-amount-${pkg.id}`} className="font-semibold text-[#C85A17]">{inr(advance)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[11px] text-[#6E6259]">{t("60% Before Arrival")}</p>
                                <p data-testid={`package-balance-amount-${pkg.id}`} className="font-semibold text-[#241D19]">{inr(balance)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-2.5">
                        <button
                            data-testid={`package-details-button-${pkg.id}`}
                            onClick={() => onDetails(pkg)}
                            className="py-3 rounded-full border border-[#E6DBCF] text-[#241D19] text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#F4EDE2] transition-colors duration-300"
                        >
                            <Eye size={15} /> {t("View Details")}
                        </button>
                        <button
                            data-testid={`package-book-button-${pkg.id}`}
                            onClick={() => onBook(pkg)}
                            className="py-3 rounded-full bg-[#241D19] text-white text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#C85A17] transition-colors duration-300 group"
                        >
                            {t("Book Now")}
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                </div>
            </motion.article>
        </Reveal>
    );
};

const DetailsModal = ({ pkg, onClose, onBook, t }) => {
    if (!pkg) return null;
    const { advance, balance } = split4060(pkg.price);
    return (
        <AnimatePresence>
            <motion.div
                data-testid="package-details-modal"
                className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
                <motion.div
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="relative bg-white w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
                >
                    <div className="relative h-56">
                        <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <button
                            onClick={onClose}
                            data-testid="package-details-close-button"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-[#241D19] hover:bg-white"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                        <div className="absolute bottom-4 left-6 right-6">
                            <span className="text-[11px] uppercase tracking-[0.2em] text-amber-300">{t(pkg.duration)} {t("Ritual")}</span>
                            <h3 className="font-serif-alt font-semibold text-2xl text-white">{t(pkg.name)}</h3>
                        </div>
                    </div>
                    <div className="p-6 sm:p-8">
                        <p className="text-sm text-[#6E6259] leading-relaxed">{t(pkg.description)}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {pkg.locations.map((loc) => (
                                <span key={loc} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#F4EDE2] text-[#241D19]">
                                    <MapPin size={11} className="text-[#C85A17]" /> {loc}
                                </span>
                            ))}
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                            <div className="bg-[#F4EDE2] rounded-xl p-4">
                                <p className="text-[11px] text-[#6E6259]">{t("Total")}</p>
                                <p className="font-display font-semibold text-[#241D19]">{inr(pkg.price)}</p>
                            </div>
                            <div className="bg-[#FFF8E7] rounded-xl p-4 border border-[#E5C158]">
                                <p className="text-[11px] text-[#6E6259]">{t("40% Advance")}</p>
                                <p className="font-display font-semibold text-[#C85A17]">{inr(advance)}</p>
                            </div>
                            <div className="bg-[#F4EDE2] rounded-xl p-4">
                                <p className="text-[11px] text-[#6E6259]">{t("60% Balance")}</p>
                                <p className="font-display font-semibold text-[#241D19]">{inr(balance)}</p>
                            </div>
                        </div>
                        <p className="mt-4 text-xs text-[#6E6259] leading-relaxed">
                            {t("Price is for the complete ritual only. Accommodation & fooding are NOT included and can be arranged separately on request.")}
                        </p>
                        <button
                            data-testid="package-details-book-button"
                            onClick={() => { onClose(); onBook(pkg); }}
                            className="mt-6 w-full py-3.5 rounded-full bg-[#C85A17] text-white font-medium hover:bg-[#9C370B] transition-colors duration-300"
                        >
                            {t("Book This Ritual")}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const CategoryBlock = ({ overline, title, subtitle, items, displayOrder, onBook, onDetails, t }) => (
    <div className="mb-20 last:mb-0">
        <Reveal>
            <Overline>{t(overline)}</Overline>
            <h3 className="mt-3 font-display text-xl sm:text-2xl lg:text-3xl text-[#241D19] tracking-tight">
                {t(title)}
            </h3>
            <p className="mt-3 max-w-2xl text-sm text-[#6E6259] leading-relaxed">{t(subtitle)}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {items.map((pkg) => (
                <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    index={displayOrder.indexOf(pkg)}
                    onBook={onBook}
                    onDetails={onDetails}
                    t={t}
                />
            ))}
        </div>
    </div>
);

export const Packages = ({ packages, onBook }) => {
    const { lang } = useLang();
    const t = (s) => tr(lang, s);
    const [detail, setDetail] = useState(null);
    const byId = Object.fromEntries(packages.map((p) => [p.id, p]));
    const oneDay = ONE_DAY_ORDER.map((id) => byId[id]).filter(Boolean);
    const multiDay = MULTI_DAY_ORDER.map((id) => byId[id]).filter(Boolean);
    const displayOrder = [...oneDay, ...multiDay];

    return (
        <section id="packages" data-testid="packages-section" className="py-20 lg:py-28 bg-[#FAF6F0]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{t("Chapter 01 — Our Rituals")}</Overline>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl text-[#241D19] tracking-tight">
                            {t("Ritual Packages")}
                        </h2>
                    </Reveal>

                <div className="mt-14">
                    <CategoryBlock
                        overline="1 Day Rituals"
                        title="1 Day Ritual Packages"
                        subtitle="Single-day rituals at the sacred vedis of Gaya — complete Pinddaan & Shradh along with special rituals like Tripindi Shradh, Narayan Bali, Nag Bali and Pitru Dosha Nivaran."
                        items={oneDay}
                        displayOrder={displayOrder}
                        onBook={onBook}
                        onDetails={setDetail}
                        t={t}
                    />
                    <CategoryBlock
                        overline="Multi-Day Rituals"
                        title="Complete Multi-Day Pinddaan & Shradh Packages"
                        subtitle="Extended 3, 5, 7 and 17-day ritual programmes at the sacred vedis of Gaya, performed by traditional Gayawal Pandas."
                        items={multiDay}
                        displayOrder={displayOrder}
                        onBook={onBook}
                        onDetails={setDetail}
                        t={t}
                    />
                </div>
            </div>
            <DetailsModal pkg={detail} onClose={() => setDetail(null)} onBook={onBook} t={t} />
        </section>
    );
};
